import { round } from 'lodash';
import * as tus from 'tus-js-client';
import { action, extendObservable } from 'mobx';
import { isRequired } from '../../../core/validations';
import FormService from '../../../core/services/FormService';
import FetchService from '../../../core/services/FetchService';
import InstructionVideoFormService from './InstructionVideoFormService';
import NotificationService from '../../../core/services/NotificationService';

class InstructionVideoUploadService {
  form = new FormService();
  submit = new FetchService();

  constructor() {
    extendObservable(this, {
      isOpen: false,
      instructionVideoId: null,
      uploadedPercentage: 0,
    });
  }

  init = action((instructionVideoId, initialValues) => {
    this.form.reset();
    this.form.setInitialValues(initialValues || {});
    this.form.validations = {
      locale: [isRequired],
      videoTitle: [isRequired],
      video: this.form.getValue('id') ? [] : [isRequired],
    };
    this.instructionVideoId = instructionVideoId;
  });

  handleSubmit = action(() => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }

    this.submit
      .fetch({
        method: 'put',
        url: `/instruction-videos/${this.instructionVideoId}`,
        body: {
          ...this.form.getValues(),
        },
      })
      .then(() => {
        if (this.submit.data) {
          if (this.form.getValue('video')) {
            const currentInstruction = this.submit.data.instructions.find(
              (instruction) => instruction.locale === this.form.getValue('locale')
            );
            this.handleUpload(this.form.getValue('video'), currentInstruction.videoUploadLink)
              .then(() => {
                NotificationService.addNotification(
                  `Instruction Video ${this.form.getValue('id') ? 'updated' : 'created'} successfully`,
                  'success'
                );
                this.form.reset();
                this.form.setInitialValues({});
                InstructionVideoFormService.handleLoad(this.instructionVideoId);
              })
              .catch(() => {
                NotificationService.addNotification(
                  `Error ${this.form.getValue('id') ? 'updating' : 'adding'} Instruction Video.`,
                  'error'
                );
              });
          } else {
            NotificationService.addNotification('Instruction Video updated successfully', 'success');
            this.form.reset();
            this.form.setInitialValues({});
            InstructionVideoFormService.handleLoad(this.instructionVideoId);
          }
        }
      });
  });

  handleUpload = action((file, uploadUrl) => {
    const url = new URL(uploadUrl);
    const signature = url.searchParams.get('AuthorizationSignature');
    const expiration = url.searchParams.get('AuthorizationExpire');
    const vid = url.searchParams.get('VideoId');
    const libId = url.searchParams.get('LibraryId');
    this.isOpen = true;
    return new Promise((resolve, reject) => {
      const uploader = new tus.Upload(file, {
        retryDelays: [0, 3000, 5000, 10000, 20000],
        endpoint: url.href.replace(url.search, ''),
        headers: {
          AuthorizationSignature: signature,
          AuthorizationExpire: expiration,
          VideoId: vid,
          LibraryId: libId,
        },
        removeFingerprintOnSuccess: true,
        metadata: {
          filename: file.name,
          filetype: file.type,
        },
        onProgress: this.handleUploadProgress,
        onError: (error) => reject(error),
        onSuccess: () => {
          InstructionVideoFormService.handleLoad(this.instructionVideoId);
          resolve();
        },
      });
      uploader.start();
    });
  });

  handleUploadProgress = action((uploadedBytest, totalBytes) => {
    const percentage = round((uploadedBytest / totalBytes) * 100, 2);
    this.uploadedPercentage = percentage;
    if (percentage >= 100) {
      this.isOpen = false;
    }
  });
}

const instructionVideoUploadService = new InstructionVideoUploadService();
export default instructionVideoUploadService;
